// Shared scalar schemas reused across REST bodies and WS frames. Defining them
// once keeps validation identical everywhere and makes the emitted JSON Schemas
// consistent for the Go contract tests.

import { z } from 'zod';

/** Standard base64 (RFC 4648) with optional padding. */
export const Base64 = z
  .string()
  .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'must be base64');

/** URL-safe base64 without padding (used for pinned-cert SPKI hashes, tokens). */
export const Base64Url = z
  .string()
  .regex(/^[A-Za-z0-9_-]+$/, 'must be base64url');

/** A user/account UUID (UUIDv4, the public identity). */
export const Uuid = z.uuid();

// Length constraints are expressed as regexes (not `.refine()`) so they survive
// into the exported JSON Schema and the Go server validates identically.

/** Ed25519 identity public key, 32 raw bytes → 44 base64 chars (43 + one `=`). */
export const Ed25519PublicKey = z
  .string()
  .regex(/^[A-Za-z0-9+/]{43}=$/, 'must be a 32-byte base64 Ed25519 public key');

/** An Ed25519 signature, 64 raw bytes → 88 base64 chars (86 + `==`). */
export const Ed25519Signature = z
  .string()
  .regex(/^[A-Za-z0-9+/]{86}==$/, 'must be a 64-byte base64 Ed25519 signature');

/** Opaque, arbitrarily-sized base64 blob (nonces, salts, encrypted key blobs). */
export const B64Blob = Base64.min(1);

/** Self-chosen display name, shown to friends only. */
export const DisplayName = z.string().max(64);

/**
 * Avatar seed: the small candidate index (0..8) a user picks at signup. The
 * "liquid mercury" avatar image is computed CLIENT-SIDE as f(userId,
 * avatarSeed); the server only stores and relays this integer — no image bytes
 * ever touch it.
 */
export const AvatarSeed = z.number().int().min(0).max(8);

/**
 * SDP is an opaque string server-side (docs/02 §4, docs/03 §6): the server does
 * no structural validation beyond size, so neither do we here.
 */
export const Sdp = z.string().min(1);

/** A JWT (access or refresh) or opaque bearer token — never structurally parsed here. */
export const Token = z.string().min(1);

/** ISO-8601 / RFC-3339 UTC timestamp string (e.g. account/created_at fields). */
export const Timestamp = z.iso.datetime();

/** A per-connection server-assigned monotonic frame sequence number. */
export const Seq = z.number().int().nonnegative();

/** Milliseconds since the Unix epoch (server clock, e.g. `sys.hello.serverTime`). */
export const EpochMillis = z.number().int().nonnegative();

/** A WS connection id (distinct from the account `sid`), used for resume. */
export const ConnId = z.string().min(1);

/** A call id (UUIDv4), scopes every `call.*` frame. */
export const CallId = z.uuid();
